import type { ListView } from '@/components/list/ViewTabs';
import { DEFAULT_PAGE_SIZE, parsePage, parsePageSize } from '@/components/list/use-list-params';
import type { components, operations } from '@/lib/api-types-expenses-suppliers';

export type SiteCategory = components['schemas']['SiteCategory'];
export type Continent = components['schemas']['Continent'];
export type CatalogQuery = NonNullable<operations['listSuppliers']['parameters']['query']>;
export type CatalogFilters = Omit<CatalogQuery, 'offset' | 'limit' | 'sort' | 'direction'>;

export const SITE_CATEGORIES: readonly SiteCategory[] = [
  'restaurant',
  'hotel',
  'bar',
  'copy_shop',
  'ncc',
  'taxi',
  'car_rental',
  'transport',
  'fuel',
  'parking',
  'shop',
  'other',
];

export const CONTINENTS: readonly Continent[] = [
  'europe',
  'asia',
  'africa',
  'north_america',
  'south_america',
  'oceania',
  'antarctica',
];

/**
 * «Stato e segni» (board 4.2): one filter in the toolbar, several parameters in the API. Everyone
 * may ask for `intermediary`; the others are for «Gestione» and the approvers (contract 0.2.1:
 * anyone else gets 403 LIST_FILTER_FORBIDDEN).
 */
export const MARK_FILTERS = [
  'to_complete',
  'to_verify',
  'flagged',
  'intermediary',
  'unusable',
] as const;
export type MarkFilter = (typeof MARK_FILTERS)[number];
export const OPEN_MARK_FILTERS: readonly MarkFilter[] = ['intermediary'];

/** The order the API whitelists; «uses» (most used first, then by name) is the default (D246). */
export const SORTS = ['uses', 'name', 'lastUsed', 'city'] as const;
export type Sort = (typeof SORTS)[number];
export const DEFAULT_SORT: Sort = 'uses';

/** The URL's filter keys. A key may hold several values, comma separated (`category=restaurant,bar`). */
export const FILTER_KEYS = ['category', 'continent', 'country', 'region', 'city', 'mark'] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

/** Everything a view may name: the filters, and for a personal view the search, order and rows. */
export const VIEW_KEYS = [...FILTER_KEYS, 'q', 'sort', 'pageSize'] as const;

export type ResultsMode = 'list' | 'map';

export interface ListState {
  readonly q: string;
  readonly filters: Readonly<Record<FilterKey, readonly string[]>>;
  readonly sort: Sort;
  readonly page: number;
  readonly pageSize: number;
  readonly mode: ResultsMode;
}

function values(raw: string | null): string[] {
  if (raw === null) return [];
  return [
    ...new Set(
      raw
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ];
}

function only<T extends string>(allowed: readonly T[], raw: readonly string[]): T[] {
  return raw.filter((value): value is T => (allowed as readonly string[]).includes(value));
}

/**
 * The list's state as the address holds it (board 4, «Stato nell'indirizzo»): `q`, the filters,
 * `sort`, `page`, `pageSize` and `view=map`. A value the API would refuse is dropped here, so a
 * mistyped link opens the list rather than an error.
 */
export function parseListState(params: URLSearchParams): ListState {
  const sort = SORTS.find((value) => value === params.get('sort')) ?? DEFAULT_SORT;
  return {
    q: (params.get('q') ?? '').trim().slice(0, 200),
    filters: {
      category: only(SITE_CATEGORIES, values(params.get('category'))),
      continent: only(CONTINENTS, values(params.get('continent'))),
      country: values(params.get('country'))
        .map((code) => code.toUpperCase())
        .filter((code) => /^[A-Z]{2}$/.test(code)),
      region: values(params.get('region')).slice(0, 1),
      city: values(params.get('city')).slice(0, 1),
      mark: only(MARK_FILTERS, values(params.get('mark'))),
    },
    sort,
    page: parsePage(params.get('page')),
    pageSize: parsePageSize(params.get('pageSize')),
    mode: params.get('view') === 'map' ? 'map' : 'list',
  };
}

/** The filters as the catalog's parameters (list, export and map alike). */
export function catalogFilters(state: Pick<ListState, 'q' | 'filters'>): CatalogFilters {
  const { category, continent, country, region, city, mark } = state.filters;
  const out: CatalogFilters = {};
  if (state.q !== '') out.q = state.q;
  if (category.length > 0) out.category = [...(category as SiteCategory[])];
  if (continent.length > 0) out.continent = [...(continent as Continent[])];
  if (country.length > 0) out.country = [...country];
  if (region[0] !== undefined) out.region = region[0];
  if (city[0] !== undefined) out.city = city[0];
  const marks = mark as readonly MarkFilter[];
  if (marks.includes('unusable')) out.status = ['unusable'];
  if (marks.includes('to_complete')) out.completeness = 'to_complete';
  const apiMarks = [
    ...(marks.includes('flagged') ? (['flagged'] as const) : []),
    ...(marks.includes('intermediary') ? (['intermediary'] as const) : []),
    ...(marks.includes('to_verify') ? (['toVerify'] as const) : []),
  ];
  if (apiMarks.length > 0) out.mark = apiMarks;
  return out;
}

/** One page of the list: the filters, the order and the page. */
export function catalogQuery(state: ListState): CatalogQuery {
  return {
    ...catalogFilters(state),
    sort: state.sort,
    offset: (state.page - 1) * state.pageSize,
    limit: state.pageSize,
  };
}

/** Anything that narrows the list: then an empty answer is «no results», not an empty catalog. */
export function isNarrowed(state: Pick<ListState, 'q' | 'filters'>): boolean {
  return state.q !== '' || FILTER_KEYS.some((key) => state.filters[key].length > 0);
}

/** Labels of the system views, already translated. */
export interface SystemViewLabels {
  readonly all: string;
  readonly lodging: string;
  readonly food: string;
  readonly toComplete: string;
  readonly toVerify: string;
  readonly flagged: string;
  readonly intermediary: string;
  readonly unusable: string;
}

/**
 * The system views (board 4.1, 4.6): for everyone «Tutti», «Alloggi», «Ristoranti e bar»; for
 * «Gestione» and the approvers also the queues by status and mark. «Più usati da te» is on the
 * boards but the contract (0.2.3) has no per-person order: it waits for the API.
 */
export function systemViews(labels: SystemViewLabels, gestione: boolean): ListView[] {
  const views: ListView[] = [
    { id: 'all', label: labels.all, params: {} },
    { id: 'lodging', label: labels.lodging, params: { category: 'hotel' } },
    { id: 'food', label: labels.food, params: { category: 'restaurant,bar' } },
  ];
  if (!gestione) return views;
  return [
    ...views,
    { id: 'to-complete', label: labels.toComplete, params: { mark: 'to_complete' } },
    { id: 'to-verify', label: labels.toVerify, params: { mark: 'to_verify' } },
    { id: 'flagged', label: labels.flagged, params: { mark: 'flagged' } },
    { id: 'intermediary', label: labels.intermediary, params: { mark: 'intermediary' } },
    { id: 'unusable', label: labels.unusable, params: { mark: 'unusable' } },
  ];
}

/** The address after choosing a view: its keys set, every other view key cleared, page one. */
export function applyView(current: URLSearchParams, view: ListView): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete('page');
  const personal = Object.keys(view.params).some(
    (key) => !(FILTER_KEYS as readonly string[]).includes(key),
  );
  // A system view keeps the search and the order, as the expense list's do; a personal view
  // brings its own (board 4.6: «tiene ricerca, filtri, ordine e righe per pagina»).
  const keys: readonly string[] = personal ? VIEW_KEYS : FILTER_KEYS;
  for (const key of keys) {
    const value = view.params[key];
    if (value === undefined || value === '') next.delete(key);
    else next.set(key, value);
  }
  return next;
}

/** What a personal view keeps of the current address. */
export function viewParams(params: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of VIEW_KEYS) {
    const value = params.get(key);
    if (value === null || value === '') continue;
    if (key === 'sort' && value === DEFAULT_SORT) continue;
    if (key === 'pageSize' && value === String(DEFAULT_PAGE_SIZE)) continue;
    out[key] = value;
  }
  return out;
}

/** «2026-10» as «ottobre 2026»; the API gives the last use as a month (contract: YYYY-MM). */
export function monthLabel(month: string | null, language: string | undefined): string | null {
  if (month === null) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat(language ?? 'it', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}
